'use client';

import * as React from 'react';

import { useSensitivity } from '@/components/ui/sensitivity-toggle';
import { formatCurrency, formatNativeCurrency } from '@/lib/utils/formatters';

export interface MoneyProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Numeric amount. Pass `null`/`undefined` to render `fallback`. */
  amount: number | null | undefined;
  /** Optional currency code passed through to `formatCurrency`. Defaults to USD. */
  currency?: string;
  /** Shown when `amount` is nullish (default: "—"). */
  fallback?: React.ReactNode;
  /**
   * Number of fraction digits. Default `2` matches `formatCurrency`. Pass `0`
   * for whole-dollar display (common in financial reports for readability).
   * **Never pass `1`** — single-decimal currency is banned project-wide.
   */
  decimals?: 0 | 2;
  unit?: 'major' | 'cents';
  notation?: 'standard' | 'compact';
  accounting?: boolean;
  nativePrecision?: boolean;
}

/**
 * <Money> — Currency text that respects the global privacy toggle.
 *
 * Renders `formatCurrency(amount, currency)` by default, or a masked form
 * (e.g. "$*****") when the user has enabled the "hide amounts" mode via the
 * header toggle.
 *
 * Prefer this over calling `formatCurrency` directly in UI that should
 * honor screen-sharing privacy mode.
 */
export function Money({
  amount,
  currency = 'USD',
  fallback = '—',
  decimals = 2,
  unit = 'major',
  notation = 'standard',
  accounting = false,
  nativePrecision = false,
  className,
  ...rest
}: MoneyProps) {
  const formatMoney = useMoneyFormatter();

  if (amount == null || Number.isNaN(amount)) {
    return (
      <span className={className} {...rest}>
        {fallback}
      </span>
    );
  }

  const formatted = formatMoney(amount, {
    currency,
    decimals,
    unit,
    notation,
    accounting,
    nativePrecision,
  });

  return (
    <span className={className} {...rest}>
      {formatted}
    </span>
  );
}

/** Currency text for sentences, with the same privacy and unit rules as Money. */
export function useMoneyFormatter() {
  const { mask } = useSensitivity();
  return React.useCallback(
    (
      amount: number,
      {
        currency = 'USD',
        decimals = 2,
        unit = 'major',
        notation = 'standard',
        accounting = false,
        nativePrecision = false,
      }: Pick<
        MoneyProps,
        | 'currency'
        | 'decimals'
        | 'unit'
        | 'notation'
        | 'accounting'
        | 'nativePrecision'
      > = {}
    ) => {
      const value = unit === 'cents' ? amount / 100 : amount;
      return mask(
        nativePrecision
          ? formatNativeCurrency(value, currency)
          : formatCurrency(value, currency, { decimals, notation, accounting })
      );
    },
    [mask]
  );
}
